import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";

// Total score thresholds (0-100)
const TOTAL_THRESHOLD_GREEN = 70;
const TOTAL_THRESHOLD_YELLOW = 40;

// Sub-score thresholds (0-25)
const SUB_THRESHOLD_GREEN = 18;
const SUB_THRESHOLD_YELLOW = 12;

// Color constants — theme tokens (resolve at render in inline styles)
const COLOR_HEALTHY = "hsl(var(--edgely-pnl-positive))";
const COLOR_WARNING = "hsl(var(--warning))";
const COLOR_ALERT = "hsl(var(--edgely-pnl-negative))";

function totalColor(total: number): string {
  if (total >= TOTAL_THRESHOLD_GREEN) return COLOR_HEALTHY;
  if (total >= TOTAL_THRESHOLD_YELLOW) return COLOR_WARNING;
  return COLOR_ALERT;
}

function subColor(score: number): string {
  if (score >= SUB_THRESHOLD_GREEN) return COLOR_HEALTHY;
  if (score >= SUB_THRESHOLD_YELLOW) return COLOR_WARNING;
  return COLOR_ALERT;
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function SubScoreRow({ label, score }: { label: string; score: number }) {
  const color = subColor(score);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-widest widget-label">
          {label}
        </p>
        <span className="font-mono text-xs font-bold" style={{ color }}>
          {score}/25
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted/30">
        <div
          className="h-1.5 rounded-full transition-all"
          style={{
            width: `${Math.min((score / 25) * 100, 100)}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}

export default function ConsistencyScoreWidget() {
  const { analyticsData, isDark, t } = useDashboardData();

  if (!analyticsData || !analyticsData.consistencyScore) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.dashboard.widgets.consistencyScore}
          isDark={isDark}
          compact
        />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.consistencyScoreMin} />
        </CardContent>
      </Card>
    );
  }

  const cs = analyticsData.consistencyScore;

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.widgets.consistencyScore}
        isDark={isDark}
        compact
      />
      <CardContent className="flex-1 flex flex-col justify-center gap-4 px-4 pb-4 pt-2">
        <div className="text-center">
          <p
            className="text-4xl font-bold font-mono leading-none"
            style={{ color: totalColor(cs.total) }}
          >
            {cs.total}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <SubScoreRow
            label={t.analytics.csWinRateStability}
            score={cs.winRateStability}
          />
          <SubScoreRow
            label={t.analytics.csRiskConsistency}
            score={cs.riskConsistency}
          />
          <SubScoreRow label={t.analytics.csNoRevenge} score={cs.revengeScore} />
          <SubScoreRow
            label={t.analytics.csDrawdownMgmt}
            score={cs.drawdownMgmt}
          />
        </div>
      </CardContent>
    </Card>
  );
}
